import React, { useState } from 'react';
import {
  Card,
  Col,
  Row,
  Typography,
  Button,
  Tabs,
  Divider,
} from "antd";
import {
  DollarOutlined,
  StockOutlined,
  FormOutlined,
  InfoCircleOutlined,
  BarChartOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import DividendForm from '../components/DividendForm';
import TransactionForm from '../components/TransactionForm';
import TextParserForm from '../components/TextParserForm';
import NHHoldingsForm from '../components/NHHoldingsForm';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const DataEntry = () => {
  const [activeTab, setActiveTab] = useState("textparser");
  const [showForm, setShowForm] = useState(false);

  const handleFormSuccess = () => {
    setShowForm(false);
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  const tabsData = [
    {
      label: "Smart Text Parser",
      value: "textparser",
      desc: "Paste notification text and auto-parse data",
      icon: <FileTextOutlined />,
      color: "#722ed1",
      examples: [
        "Paste entire brokerage notification text",
        "Supports NH투자증권 and 한국투자증권",
        "Auto-detects dividend or transaction data",
        "Fills form automatically"
      ]
    },
    {
      label: "NH Holdings Management",
      value: "nhholdings",
      desc: "Manage NH Investment & Securities holdings",
      icon: <StockOutlined />,
      color: "#52c41a",
      examples: [
        "Account Type: NH Investment & Securities Trust Account",
        "Stock Name: Samsung Electronics (005930)",
        "Quantity: 100 shares",
        "Average Buy Price: 75,000 KRW"
      ]
    },
    {
      label: "Dividend Entry",
      value: "dividend",
      desc: "Enter ETF dividend payment records",
      icon: <DollarOutlined />,
      color: "#1890ff",
      examples: [
        "Stock Name: KODEX US Dividend Dow Jones Target Covered Call",
        "Account Number: 64****26-01",
        "Dividend Amount: 144,180 KRW",
        "Payment Date: 2025-08-04"
      ]
    },
    {
      label: "Transaction Entry",
      value: "transaction",
      desc: "Enter stock trading execution records",
      icon: <StockOutlined />,
      color: "#fa8c16",
      examples: [
        "Transaction Type: Cash Buy Execution",
        "Stock Name: KODEX US Dividend Dow Jones Target Covered Call (483290)",
        "Quantity: 14 shares",
        "Execution Price: 8,970 KRW"
      ]
    }
  ];

  if (showForm) {
    return (
      <div className="layout-content">
        {activeTab === "textparser" ? (
          <TextParserForm 
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        ) : activeTab === "nhholdings" ? (
          <NHHoldingsForm 
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        ) : activeTab === "dividend" ? (
          <DividendForm 
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        ) : (
          <TransactionForm 
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="layout-content">
        {/* Header - matching Dividend page style */}
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card>
              <div>
                <FormOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#722ed1' }} />
                <Title level={2} style={{ display: 'inline', margin: 0 }}>
                  Data Entry
                </Title>
              </div>
              <Text style={{ color: '#666', marginTop: '8px', display: 'block' }}>
                Enter dividend payments and stock trading records to manage your portfolio.
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Main Input Area */}
        <Row gutter={[24, 0]} style={{ marginTop: '24px' }}>
          <Col xs={24} className="mb-24">
            <Card bordered={false} className="criclebox cardbody">
              <div className="project-ant">
                <div>
                  <Title level={5}>Data Entry Options</Title>
                  <Text className="lastweek">
                    Choose the type of data you want to enter
                  </Text>
                </div>
              </div>
              
              <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab}
                size="large"
                tabBarStyle={{ 
                  marginBottom: '24px',
                  paddingLeft: '24px',
                  paddingRight: '24px'
                }}
              >
                {tabsData.map(({ value, label, desc, examples, icon, color }) => (
                  <TabPane 
                    tab={
                      <span>
                        {icon}
                        <span style={{ marginLeft: '8px' }}>{label}</span>
                      </span>
                    } 
                    key={value}
                  >
                    <Row gutter={[24, 24]}>
                      {/* Description Area */}
                      <Col xs={24} md={12}>
                        <div style={{ padding: '24px' }}>
                          <Title level={4} style={{ color: color, marginBottom: '16px' }}>
                            {icon}
                            <span style={{ marginLeft: '8px' }}>{desc}</span>
                          </Title>
                          
                          <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
                            You can enter data in the following format:
                          </Paragraph>

                          <Card size="small" style={{ backgroundColor: '#f6f8fa' }}>
                            <Title level={5} style={{ marginBottom: '12px' }}>
                              📝 Input Examples:
                            </Title>
                            {examples.map((example, index) => (
                              <div key={index} style={{ marginBottom: '8px' }}>
                                <Text code style={{ fontSize: '13px' }}>
                                  • {example}
                                </Text>
                              </div>
                            ))}
                          </Card>
                        </div>
                      </Col>

                      {/* Action Button Area */}
                      <Col xs={24} md={12}>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          height: '100%',
                          minHeight: '300px',
                          textAlign: 'center',
                          padding: '24px'
                        }}>
                          <div style={{ 
                            width: '120px', 
                            height: '120px', 
                            borderRadius: '50%', 
                            backgroundColor: color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '24px'
                          }}>
                            {React.cloneElement(icon, { 
                              style: { fontSize: '48px', color: '#fff' } 
                            })}
                          </div>
                          
                          <Title level={4} style={{ marginBottom: '16px' }}>
                            Start Data Entry
                          </Title>
                          
                          <Button
                            type="primary"
                            size="large"
                            style={{ 
                              backgroundColor: color,
                              borderColor: color,
                              height: '48px',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              minWidth: '200px'
                            }}
                            onClick={() => setShowForm(true)}
                          >
                            {value === "textparser" ? "Start Smart Parser" : 
                             value === "nhholdings" ? "Manage NH Holdings" :
                             value === "dividend" ? "Enter Dividend" : "Enter Transaction"}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </TabPane>
                ))}
              </Tabs>
            </Card>
          </Col>
        </Row>

        {/* Help Cards */}
        <Row gutter={[24, 0]}>
          <Col xs={24} md={12} className="mb-24">
            <Card bordered={false} className="criclebox h-full">
              <div className="timeline-box">
                <Title level={5} style={{ color: '#1890ff', marginBottom: '16px' }}>
                  <InfoCircleOutlined style={{ marginRight: '8px' }} />
                  Input Tips
                </Title>
                <Text className="lastweek" style={{ marginBottom: 24 }}>
                  Guidelines for data entry
                </Text>
                <div style={{ lineHeight: '2' }}>
                  <Paragraph>
                    <Text strong>• Stock Selection:</Text> Choose from existing stocks in dropdown or enter new stock information directly.
                  </Paragraph>
                  <Paragraph>
                    <Text strong>• Account Selection:</Text> Select from existing accounts or add new account information.
                  </Paragraph>
                  <Paragraph>
                    <Text strong>• Required Fields:</Text> All required fields must be filled to save the data.
                  </Paragraph>
                  <Paragraph>
                    <Text strong>• Auto Calculation:</Text> Total transaction amount is automatically calculated.
                  </Paragraph>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} md={12} className="mb-24">
            <Card bordered={false} className="criclebox h-full">
              <div className="timeline-box">
                <Title level={5} style={{ color: '#52c41a', marginBottom: '16px' }}>
                  <BarChartOutlined style={{ marginRight: '8px' }} />
                  Data Usage
                </Title>
                <Text className="lastweek" style={{ marginBottom: 24 }}>
                  How your data is utilized
                </Text>
                <div style={{ lineHeight: '2' }}>
                  <Paragraph>
                    <Text strong>• Dashboard Integration:</Text> Entered data is visualized in charts on the dashboard.
                  </Paragraph>
                  <Paragraph>
                    <Text strong>• Statistical Analysis:</Text> Monthly dividend statistics and transaction history analysis.
                  </Paragraph>
                  <Paragraph>
                    <Text strong>• Performance Tracking:</Text> Real-time portfolio performance tracking and management.
                  </Paragraph>
                  <Paragraph>
                    <Text strong>• Report Generation:</Text> Investment reports generated based on entered data.
                  </Paragraph>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default DataEntry;
